/**
 * PixiMount — owner of the PixiJS Application lifecycle.
 *
 * # What this file owns
 *
 *   1. **Application construction** — instantiate `pixi.js`'s `Application`,
 *      run its async `init()` with sensible defaults, and append the produced
 *      canvas to a caller-provided container element.
 *   2. **Lifecycle invariants** — mount is idempotent (StrictMode-safe).
 *      Dispose tears down the renderer cleanly and is also idempotent.
 *      Accessors (`getStage`, `getApp`, `getCanvas`) throw with descriptive
 *      errors when called before mount or after dispose.
 *   3. **Resize handling** — delegated to PixiJS's built-in `resizeTo`
 *      option. The renderer auto-resizes when the container resizes; no
 *      manual `window` resize listener is needed.
 *   4. **CullerPlugin registration** — module-level call to
 *      `extensions.add(CullerPlugin)` so every Application benefits from
 *      automatic viewport culling. Layer 3 (TileRenderer) and beyond
 *      set `cullable = true` on their containers/sprites; CullerPlugin
 *      handles the rest before each render.
 *
 * # What this file does NOT own
 *
 *   - Texture / sprite atlas loading (Layer 2 — `atlasLoader.ts`)
 *   - Tile rendering, isometric projection (Layer 3 — `tileRenderer.ts`)
 *   - Sprite rendering, animation, palette swap (Layer 4 — `spriteRenderer.ts`)
 *   - Camera follow / bounds clamping (Layer 5 — `cameraController.ts`)
 *   - Per-frame logic — Layers 4-5 register their own `app.ticker.add(...)`
 *     callbacks via `getApp()`. PixiMount itself does not run game logic.
 *   - React lifecycle — this is a plain class, designed to live inside a
 *     `useRef` in CombatScreen (Layer 6) and constructed/disposed by a
 *     standard `useEffect` cleanup pattern. Mirrors the SaveController
 *     precedent in `App.tsx`.
 *
 * # Why a class, not a hook
 *
 * Hooks would couple this to React's lifecycle (re-renders, StrictMode
 * double-invoke). The class is React-agnostic: a long-lived owner of a
 * GPU/canvas resource that's wired into React via a thin `useEffect`
 * shim in Layer 6. Same pattern as `SaveController` in the persistence
 * layer; that pattern proved out across Phase A + B + C and is what we
 * mirror here.
 *
 * # StrictMode safety
 *
 * `main.tsx` enables `<StrictMode>`, which causes effects to be invoked
 * twice in dev to surface cleanup bugs. PixiMount is StrictMode-safe via
 * the `mounted` flag: a second `mount()` call before `dispose()` is a
 * no-op. The companion guarantee is that `dispose()` fully releases the
 * renderer and its DOM node so a subsequent `mount()` after dispose can
 * legitimately rebuild from scratch.
 *
 * # Dispose semantics
 *
 * Calls `app.destroy({ removeView: true })` — destroys the renderer and
 * removes the canvas from the DOM. We deliberately do NOT pass the
 * `{ children: true, texture: true, textureSource: true }` option here:
 * Phase 2 doesn't yet load textures (Layer 2 will), and that team will
 * own its own atlas-lifecycle disposal. Aggressive teardown can be
 * revisited at end of Phase 2 if dispose performance matters.
 *
 * # Renderer choice (LOCKED v2.10): WebGL over WebGPU for v1
 *
 * WebGL has 99%+ browser coverage; WebGPU is rolling out and not yet
 * universal in Firefox. v1 ships WebGL for compatibility. The
 * `preference` option can be flipped at runtime in a future phase if
 * we add a graphics-tier toggle.
 *
 * # Resolution policy
 *
 * `resolution: min(devicePixelRatio, 2)`. On a 4K monitor with DPR=2,
 * this gives sharp output; on a phone with DPR=3, capping at 2 saves
 * GPU and memory at imperceptible visual cost. Standard convention.
 *
 * # CullerPlugin (added Layer 3)
 *
 * `extensions.add(CullerPlugin)` runs once at module load. It registers
 * a render-time hook that auto-culls children of any Container marked
 * `cullable = true` whose bounds fall outside the renderer's screen
 * rectangle. This is the idiomatic PixiJS v8 viewport-culling pattern
 * and future-proofs the renderer for arbitrarily large arenas, mobile
 * devices, and any future mode (raids, federation strategic UI, story
 * scenes) without a renderer rewrite.
 *
 * Cost when nothing is cullable: zero. Cost per cullable item: one
 * bounds-check vs the screen rect per frame. Genuinely free at v1
 * entity counts; even at horde-mode densities (50-100+ entities) the
 * per-frame cost is sub-millisecond on modern hardware.
 *
 * # Test contract
 *
 * The companion test file uses `vi.mock('pixi.js', ...)` to replace
 * `Application` with a recording stub. Tests verify the LIFECYCLE
 * contract (mount/dispose/idempotency/accessor errors) without ever
 * needing a real WebGL context. Real rendering is verified in the
 * Phase 2 manual browser smoke test, not in vitest.
 *
 * @see {@link ../components/CombatScreen} — Layer 6 React mount point
 * @see {@link ./atlasLoader} — Layer 2 (next layer)
 * @see {@link ./tileRenderer} — Layer 3 — uses CullerPlugin via cullable=true
 * @see {@link ../persistence/saveController} — class-with-DI precedent
 */

import { Application, CullerPlugin, extensions, type Container } from 'pixi.js';

// ─────────────────────────────────────────────────────────────────────────────
// Module-level: register CullerPlugin once, on first import.
//
// PixiJS v8's extension system is idempotent — calling extensions.add
// twice is a no-op for the same extension. Safe under StrictMode and
// hot-module-replace.
// ─────────────────────────────────────────────────────────────────────────────

extensions.add(CullerPlugin);

/**
 * Configuration for `PixiMount`.
 *
 * Required: `container` — the DOM element the canvas attaches to AND the
 * element PixiJS auto-resizes to.
 *
 * Optional knobs have sensible defaults that match v1 design (WebGL,
 * black background, capped DPR). Override sparingly.
 */
export interface PixiMountOptions {
  /**
   * The DOM container the PixiJS canvas is appended to. Must be in the
   * document tree at the time `mount()` is called. PixiJS auto-resizes
   * the renderer to match this element's `clientWidth` / `clientHeight`
   * via the `resizeTo` option.
   */
  readonly container: HTMLDivElement;

  /**
   * Background color as a 24-bit RGB integer (e.g. `0x000000` for black,
   * `0x1099bb` for the PixiJS default). Defaults to `0x000000` to match
   * the locked Aevon's Wake aesthetic (orange/black/white/gunmetal —
   * black is the canvas baseline).
   */
  readonly backgroundColor?: number;

  /**
   * Renderer resolution multiplier. Defaults to
   * `Math.min(devicePixelRatio, 2)`. Higher values produce sharper
   * output on high-DPI displays at GPU cost. The cap at 2 is a standard
   * convention: above 2x DPR, perceptual gains are minimal but GPU cost
   * scales quadratically.
   */
  readonly resolution?: number;

  /**
   * Renderer backend preference. Defaults to `'webgl'` for broad
   * compatibility (Firefox WebGPU support is still rolling out as of
   * v1 launch window). Pass `'webgpu'` only when targeting environments
   * with confirmed WebGPU support.
   */
  readonly preference?: 'webgl' | 'webgpu';

  /**
   * Whether to attach PixiJS's `resizeTo` to the container. Defaults to
   * `true`. Tests can pass `false` to skip the configuration; production
   * always wants the auto-resize behavior.
   */
  readonly attachResize?: boolean;
}

/**
 * Reasonable defaults applied when options omit a value. Exported for
 * tests and for any consumer that wants to inspect the active baseline.
 */
export const PIXI_MOUNT_DEFAULTS = {
  backgroundColor: 0x000000,
  preference: 'webgl' as const,
  attachResize: true,
} as const;

/**
 * Compute the default resolution based on the current device pixel
 * ratio, capped at 2. Exposed so tests can verify the policy without
 * needing to read it out of an internal constant.
 *
 * `globalThis.devicePixelRatio` is undefined in pure Node (no `window`),
 * so we fall back to `1` in that case.
 */
export function computeDefaultResolution(): number {
  // Use globalThis.devicePixelRatio (set on `window` in browsers, may be
  // unset in pure Node). We do not destructure to keep the access lazy.
  const dpr =
    typeof globalThis !== 'undefined' &&
    typeof (globalThis as { devicePixelRatio?: number }).devicePixelRatio ===
      'number'
      ? (globalThis as { devicePixelRatio?: number }).devicePixelRatio!
      : 1;
  return Math.min(dpr, 2);
}

/**
 * Owner of the PixiJS `Application` lifecycle. Construct once per combat
 * screen instance; call `mount()` after construction; call `dispose()` on
 * teardown. All accessors are valid only between `mount` and `dispose`.
 */
export class PixiMount {
  private readonly options: PixiMountOptions;
  private app: Application | null = null;
  private mounted = false;
  private mounting = false;
  private disposed = false;

  constructor(options: PixiMountOptions) {
    this.options = options;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Construct and initialize the PixiJS `Application`, then append its
   * canvas to the configured container.
   *
   * Idempotent: a second call before `dispose()` is a no-op (StrictMode
   * double-invoke is safe). Re-calling after `dispose()` throws — the
   * instance is single-use; consumers that need a fresh renderer should
   * construct a new `PixiMount`.
   *
   * Concurrent calls (a second `mount()` while the first is still in its
   * async `init()`) coalesce: the second call awaits the same `init()`
   * promise rather than starting a second one.
   *
   * @throws Error if called after `dispose()`
   */
  async mount(): Promise<void> {
    if (this.disposed) {
      throw new Error(
        'PixiMount: cannot mount after dispose; construct a new instance instead',
      );
    }
    if (this.mounted) return;

    // Concurrent-mount coalescing: if a previous call is still running,
    // wait for it. We accomplish this by spinning until the in-flight
    // mount finishes — but we only check the flag, not loop on it
    // (single-threaded JS guarantees the flag flips between awaits).
    if (this.mounting) {
      // Spin-wait via microtask yield. In practice this loop runs at
      // most a handful of iterations because `init()` resolves on the
      // next microtask tick after the WebGL context creates.
      while (this.mounting) {
        await Promise.resolve();
      }
      return;
    }

    this.mounting = true;
    try {
      const app = new Application();
      const initOptions: Parameters<Application['init']>[0] = {
        backgroundColor:
          this.options.backgroundColor ?? PIXI_MOUNT_DEFAULTS.backgroundColor,
        resolution: this.options.resolution ?? computeDefaultResolution(),
        preference:
          this.options.preference ?? PIXI_MOUNT_DEFAULTS.preference,
        // PixiJS v8 auto-detects width/height from `resizeTo` when set.
        // When `attachResize` is false, we provide a default width/height
        // matching the container's current size at mount time so the
        // renderer has SOMETHING valid to render against in tests.
        ...(this.options.attachResize !== false
          ? { resizeTo: this.options.container }
          : {
              width: this.options.container.clientWidth || 1,
              height: this.options.container.clientHeight || 1,
            }),
      };

      await app.init(initOptions);

      // StrictMode race guard. React 18+'s <StrictMode> double-invokes
      // effects in dev (mount → cleanup → remount) to surface cleanup
      // bugs. `dispose()` can fire while this method is awaiting
      // `app.init()`. When dispose runs at that moment, `this.app` is
      // still `null` (set only AFTER the await below), so the dispose
      // branch `if (this.app !== null) app.destroy(...)` does nothing
      // — it just flips `this.disposed = true` and returns. Without
      // this check, mount() would then resume after init, append the
      // orphan canvas to the DOM, and set `this.app` — leaving a
      // disposed PixiMount with an active Application + canvas in the
      // DOM and no owner to tear it down. Under StrictMode double-mount
      // this manifests as TWO canvases stacked, with the first orphan
      // blocking visibility of the second (working) canvas.
      //
      // Fix: after the async init resolves, check the disposal flag.
      // If we got disposed during init, destroy the orphan app cleanly
      // (its canvas isn't in the DOM yet, so `removeView: false`) and
      // bail before any state mutation that would re-attach to the
      // disposed instance.
      if (this.disposed) {
        try {
          app.destroy({ removeView: false });
        } catch (err) {
          // Best-effort cleanup of the orphan. The disposed flag is
          // already set, so the instance is permanently retired even
          // if destroy throws.
          console.warn('[PixiMount] orphan app destroy threw:', err);
        }
        return;
      }

      this.options.container.appendChild(app.canvas);
      this.app = app;
      this.mounted = true;
    } finally {
      this.mounting = false;
    }
  }

  /**
   * Tear down the PixiJS renderer, remove the canvas from the DOM, and
   * release internal references.
   *
   * Idempotent: safe to call multiple times. Calls after the first are
   * no-ops. The instance is permanently retired after the first dispose;
   * accessors throw, and `mount()` rejects.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.mounted = false;

    if (this.app !== null) {
      // `removeView: true` removes the canvas from the DOM (PixiJS does
      // this internally via `canvas.parentNode?.removeChild(canvas)`).
      // We deliberately do NOT pass aggressive children/texture teardown
      // options here — that's the responsibility of layers that own
      // those resources (atlas loader in Layer 2 will manage its own
      // texture disposal).
      this.app.destroy({ removeView: true });
      this.app = null;
    }
  }

  // ───────────────────────────────────────────────────────────────────────
  // Accessors — valid only between mount and dispose
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Returns the live PixiJS `Application` instance. Used by Layers 4-5
   * to register `ticker.add(...)` callbacks for per-frame work.
   *
   * @throws Error if called before `mount()` resolves or after `dispose()`
   */
  getApp(): Application {
    if (this.app === null) {
      throw new Error(
        this.disposed
          ? 'PixiMount.getApp: instance has been disposed'
          : 'PixiMount.getApp: call mount() first',
      );
    }
    return this.app;
  }

  /**
   * Returns the root `Container` of the scene graph (PixiJS's
   * `app.stage`). Used by Layers 3-5 to attach their containers as
   * children. Note: only `Container` instances can have children in
   * PixiJS v8 — `Sprite`, `Graphics`, etc. cannot. Layers that need
   * grouping must use a `Container`.
   *
   * @throws Error if called before `mount()` resolves or after `dispose()`
   */
  getStage(): Container {
    return this.getApp().stage;
  }

  /**
   * Returns the canvas element PixiJS renders into. Used by Layer 5
   * (camera controller) to attach pointer / wheel listeners for camera
   * pan / zoom in a future phase, and by Phase 3 to attach combat input
   * listeners (mouse-aim direction, click events).
   *
   * @throws Error if called before `mount()` resolves or after `dispose()`
   */
  getCanvas(): HTMLCanvasElement {
    return this.getApp().canvas;
  }

  // ───────────────────────────────────────────────────────────────────────
  // Diagnostic / test introspection (do not use in production code)
  // ───────────────────────────────────────────────────────────────────────

  /**
   * Test-only utility: returns the current lifecycle state.
   *
   * Returns one of `'pre-mount'`, `'mounting'`, `'mounted'`, `'disposed'`.
   * Production code should not branch on this — use `getApp()` and let
   * it throw with the descriptive error if the lifecycle isn't right.
   */
  _lifecycleStateForTests(): 'pre-mount' | 'mounting' | 'mounted' | 'disposed' {
    if (this.disposed) return 'disposed';
    if (this.mounted) return 'mounted';
    if (this.mounting) return 'mounting';
    return 'pre-mount';
  }
}
